import { Alert, Button } from "@opowiedzo/ui";
import {
  useCustom,
  useCustomMutation,
  useGetIdentity,
  useInvalidate,
  useOne,
} from "@refinedev/core";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { Badge } from "@/components/Badge";
import { Labeled, TextInput } from "@/components/Form";
import {
  BackLink,
  Card,
  CardNote,
  DefinitionList,
  DetailGrid,
  ErrorBox,
  Loading,
  PageHeader,
} from "@/components/Page";
import { deliveryStatus, formatDateTime, inboundStatus, plural } from "@/lib/format";
import type { AdminIdentity } from "@/providers/authProvider";
import type { InboundRow } from "./list";

interface InboundDetail extends InboundRow {
  provider: string;
  // Sama długość surowej wiadomości. Treść idzie osobną trasą, za content_access.
  raw_length: number;
  assigned_at: string | null;
  rejection_reason: string | null;
  memoir_id: string | null;
  memoir_title: string | null;
}

interface InboundContent extends Record<string, unknown> {
  body: string;
}

interface MemoirOption {
  id: string;
  title: string;
  storyteller_name: string | null;
}

interface DeliveryOption {
  id: string;
  question: string | null;
  status: string;
  scheduled_at: string;
}

interface MemoirSearchResult extends Record<string, unknown> {
  items: MemoirOption[];
}

interface DeliverySearchResult extends Record<string, unknown> {
  items: DeliveryOption[];
}

export function InboxShowPage() {
  const { id = "" } = useParams();
  const { query } = useOne<InboundDetail>({ resource: "inbound-messages", id });
  const { data: identity } = useGetIdentity<AdminIdentity | null>();
  const message = query.data?.data;

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!message) return null;

  const status = inboundStatus(message.status);

  return (
    <>
      <BackLink to="/skrzynka">Skrzynka</BackLink>
      <PageHeader
        title="Wiadomość do przypisania"
        description={message.from_email}
        actions={<Badge tone={status.tone}>{status.label}</Badge>}
      />

      <DetailGrid
        main={
          <ContentPanel
            messageId={message.id}
            contentAccess={identity?.content_access ?? false}
            rawLength={message.raw_length}
            attachmentsCount={message.attachments_count}
          />
        }
        side={
          <>
            <Card title="Skąd przyszła">
              <DefinitionList
                rows={[
                  ["Nadawca", message.from_email],
                  ["Temat", message.subject ?? "-"],
                  ["Przyszła", formatDateTime(message.created_at)],
                  ["Rozstrzygnięta", formatDateTime(message.assigned_at)],
                  [
                    "Opowieść",
                    message.memoir_id ? (
                      <Link
                        to={`/opowiesci/${message.memoir_id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        {message.memoir_title ?? "otwórz"}
                      </Link>
                    ) : (
                      <span className="text-ink-soft">jeszcze nie przypisana</span>
                    ),
                  ],
                  ["Powód odrzucenia", message.rejection_reason ?? "-"],
                ]}
              />
            </Card>

            <AssignPanel
              message={message}
              allowed={(identity?.permissions ?? []).includes("inbox.assign")}
            />
          </>
        }
      />
    </>
  );
}

function ContentPanel({
  messageId,
  contentAccess,
  rawLength,
  attachmentsCount,
}: {
  messageId: string;
  contentAccess: boolean;
  rawLength: number;
  attachmentsCount: number;
}) {
  // Treść pobieramy dopiero po kliknięciu. Każde takie żądanie zostawia wpis
  // w audycie, więc nie wolno go wysyłać "na wszelki wypadek" przy wejściu.
  const [pokaz, setPokaz] = useState(false);
  const { query } = useCustom<InboundContent>({
    url: `/inbound-messages/${messageId}/content`,
    method: "get",
    queryOptions: { enabled: pokaz },
  });

  return (
    <Card title="Treść">
      {!contentAccess ? (
        <p className="text-base text-ink-soft">
          Treść jest zamknięta. Wgląd wymaga osobnego uprawnienia, które superadmin włącza na czas
          konkretnego zgłoszenia. To odpowiedź narratora, nie materiał roboczy.
        </p>
      ) : rawLength === 0 ? (
        <p className="text-base text-ink-soft">Ta wiadomość przyszła bez treści.</p>
      ) : !pokaz ? (
        <Button onClick={() => setPokaz(true)}>Pokaż treść (zostanie zapisane w audycie)</Button>
      ) : query.isLoading ? (
        <Loading />
      ) : query.error ? (
        <ErrorBox error={query.error} />
      ) : (
        <p className="text-base leading-relaxed whitespace-pre-line text-ink">
          {query.data?.data.body || "(pusta treść)"}
        </p>
      )}

      {attachmentsCount > 0 && (
        <div className="mt-3.5">
          <Alert tone="info">
            {`Wiadomość miała ${attachmentsCount} ${plural(
              attachmentsCount,
              "załącznik",
              "załączniki",
              "załączników",
            )}, ale ich zawartość nie została zapisana - zdjęcia trafiają do magazynu tylko wtedy, gdy wiadomość od razu pasuje do pytania. Jeśli to były fotografie, poproś rodzinę o wgranie ich w panelu.`}
          </Alert>
        </div>
      )}
    </Card>
  );
}

function AssignPanel({ message, allowed }: { message: InboundDetail; allowed: boolean }) {
  const invalidate = useInvalidate();
  const { mutate, mutation } = useCustomMutation();
  const [szukaj, setSzukaj] = useState("");
  const [memoirId, setMemoirId] = useState("");
  const [powod, setPowod] = useState("");

  // Szukamy dopiero od trzech znaków - krótszy ciąg zwróciłby całą bazę.
  const memoirs = useCustom<MemoirSearchResult>({
    url: "/memoirs",
    method: "get",
    config: { query: { query: szukaj, limit: "10" } },
    queryOptions: { enabled: szukaj.trim().length >= 3 },
  });

  const deliveries = useCustom<DeliverySearchResult>({
    url: "/prompt-deliveries",
    method: "get",
    config: {
      query: { memoir_id: memoirId, limit: "20", sort: "scheduled_at", order: "desc" },
    },
    queryOptions: { enabled: memoirId !== "" },
  });

  const odswiez = () =>
    invalidate({ resource: "inbound-messages", invalidates: ["detail", "list"], id: message.id });

  const juzObsluzona = message.status === "assigned" || message.status === "rejected";

  if (!allowed) {
    return (
      <Card title="Przypisanie">
        <p className="text-base text-ink-soft">Twoja rola nie obejmuje przypisywania wiadomości.</p>
      </Card>
    );
  }

  return (
    <Card title="Przypisanie">
      {juzObsluzona ? (
        <p className="text-base text-ink-soft">
          Ta wiadomość jest już rozstrzygnięta. Nic nie zostało skasowane - treść zostaje w bazie.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <Labeled label="Znajdź Opowieść">
            <TextInput
              type="search"
              value={szukaj}
              placeholder="imię narratora albo jego adres"
              onChange={(event) => setSzukaj(event.target.value)}
            />
          </Labeled>

          {memoirs.query.isFetching && <p className="text-sm text-ink-soft">Szukam...</p>}
          {(memoirs.query.data?.data.items ?? []).length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {memoirs.query.data?.data.items.map((memoir) => (
                <li key={memoir.id}>
                  <button
                    type="button"
                    onClick={() => setMemoirId(memoir.id)}
                    className={`w-full rounded-card border px-3 py-2.25 text-left text-base transition-colors ${
                      memoirId === memoir.id
                        ? "border-accent bg-accent/10 text-accent"
                        : "border-line bg-surface text-ink hover:border-ink-soft"
                    }`}
                  >
                    {memoir.title}
                    <span className="block text-xs text-ink-soft">
                      {memoir.storyteller_name ?? "bez narratora"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {memoirId && (
            <>
              <p className="text-base text-ink-soft">Do którego pytania to odpowiedź?</p>
              {deliveries.query.isFetching ? (
                <p className="text-sm text-ink-soft">Wczytuję wysyłki...</p>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {(deliveries.query.data?.data.items ?? []).map((delivery) => {
                    const stan = deliveryStatus(delivery.status);
                    return (
                      <li key={delivery.id}>
                        <button
                          type="button"
                          disabled={mutation.isPending}
                          onClick={() =>
                            mutate(
                              {
                                url: `/inbound-messages/${message.id}/assign`,
                                method: "post",
                                values: { prompt_delivery_id: delivery.id },
                                successNotification: false,
                                errorNotification: false,
                              },
                              { onSuccess: odswiez },
                            )
                          }
                          className="w-full rounded-card border border-line bg-surface px-3 py-2.25 text-left text-base text-ink transition-colors hover:border-ink-soft disabled:opacity-60"
                        >
                          {delivery.question ?? "(pytanie bez treści)"}
                          <span className="mt-0.5 block text-xs text-ink-soft">
                            {formatDateTime(delivery.scheduled_at)} - {stan.label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}

          {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}

          <hr className="border-t border-line" />
          <Labeled label="Albo odrzuć - z powodem">
            <TextInput
              value={powod}
              placeholder="np. newsletter, nie odpowiedź narratora"
              onChange={(event) => setPowod(event.target.value)}
            />
          </Labeled>
          <Button
            className="self-start"
            disabled={powod.trim().length < 3 || mutation.isPending}
            onClick={() =>
              mutate(
                {
                  url: `/inbound-messages/${message.id}/reject`,
                  method: "post",
                  values: { reason: powod.trim() },
                  successNotification: false,
                  errorNotification: false,
                },
                { onSuccess: odswiez },
              )
            }
          >
            Odrzuć wiadomość
          </Button>
          <CardNote>
            Odrzucenie niczego nie kasuje. Treść zostaje w bazie razem z powodem, bo pomyłka tutaj
            znaczyłaby wyrzucenie czyjegoś wspomnienia.
          </CardNote>
        </div>
      )}
    </Card>
  );
}
